import { ComponentEntity, stringifyEntityRef } from "@backstage/catalog-model";
import { AppEnvironment, MessagingResource, MessagingTopology } from "@internal/open-catalog";
import { Button, Card, CardContent, createStyles, Divider, FormControlLabel, IconButton, InputAdornment, ListItem, ListItemIcon, ListItemText, makeStyles, MenuItem, Switch, TextField, Theme, Typography } from '@material-ui/core';
import AspectRatioIcon from '@material-ui/icons/AspectRatio';
import SearchIcon from '@material-ui/icons/Search'; 
import ClearIcon from '@material-ui/icons/Clear'; 
import React, { useEffect } from 'react';
import { TopologyView } from "./components/topology";
import { ButtonExportTopology } from "./components/rabbitmq";
import useDebounce from "react-use/lib/useDebounce";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    divider: { marginTop: '1em' },
    header: {
      alignItems: 'center',
      padding: 16,
      paddingBottom: 0
    },
    headerForm: {
      alignItems: 'center',
      '& > *': {
        margin: theme.spacing(1),
        minWidth: 175,
        minHeight: 40
      }
    },
    fullScreen: {
      position: 'fixed',
      top: 0,
      left: 0,
      height: 'calc(100vh - 32px)',
      width: 'calc(100vw - 32px)',
      margin: 16,
      zIndex: 10000
    }
  })
)

export const SystemMessagingTopologyPage = (props: { services: ComponentEntity[], resources: MessagingResource[] }) => {

  const { services, resources } = props;

  const classes = useStyles();

  const [searchValue, setSearchValue] = React.useState('');
  const [debouncedSearchValue, setDebouncedSearchValue] = React.useState('');
  const [service, setService] = React.useState(services[0]);
  const [topology, setTopology] = React.useState<MessagingTopology>();
  const [autoRefresh, setAutoRefresh] = React.useState(false);
  const [focusMode, setFocusMode] = React.useState(true);
  const [fullScreen, setFullScreen] = React.useState(false);
  const [environment, setEnvironment] = React.useState<AppEnvironment>('development');

  useDebounce(
    () => {
      setDebouncedSearchValue(searchValue.toLowerCase());
    },
    300,
    [searchValue],
  );

  const handleSearchValueChange = (value: string) => {
    setSearchValue(value.toLowerCase());
  };

  const handleServiceChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const ref = event.target.value as any;
    const s = services.find(component => stringifyEntityRef(component) === ref);
    setService(s as any);
  };

  const handleEnvironmentChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEnvironment(event.target.value as any);
  };

  const handleAutoRefreshChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setAutoRefresh(event.target.checked);
  };

  const handleFocusModeChange = (_event: React.ChangeEvent<HTMLInputElement>) => {
    setFocusMode(!focusMode);
  };

  const handleFullScreen = (_event: React.MouseEvent) => {
    setFullScreen(!fullScreen);
  };

  useEffect(() => {
    const resource = resources
      .filter(r => r.spec.environment === environment)
      .find(r => r.spec.dependencyOf.includes(stringifyEntityRef(service)));

    if (resource) {
      setTopology(resource.spec.topology);
    } else {
      setTopology(undefined);
    }
  }, [service, environment, resources]);

  return (
    <>
      <Card className={fullScreen ? classes.fullScreen : ''}>
        <ListItem className={classes.header}>
          <ListItemText>
            <form noValidate autoComplete="off" className={classes.headerForm}>
              <TextField
                label="Search"
                value={searchValue}
                onChange={e => {
                  handleSearchValueChange(e.target.value);
                }}
                variant="outlined"
                size="small"
                placeholder="ex: orders"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => handleSearchValueChange('')}
                        size="small"
                      >
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                select
                label="Service"
                value={stringifyEntityRef(service)}
                onChange={handleServiceChange}
                variant="outlined"
                size="small"
              >
                {services.map(component => (
                  <MenuItem
                    key={component.metadata.uid}
                    value={stringifyEntityRef(component)}
                  >
                    {component.metadata.description || component.metadata.name}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                select
                label="Environment"
                value={environment}
                onChange={handleEnvironmentChange}
                variant="outlined"
                size="small"
              >
                {['development', 'staging', 'production'].map(env => (
                  <MenuItem key={env} value={env}>
                    {env}
                  </MenuItem>
                ))}
              </TextField>

              <FormControlLabel
                control={
                  <Switch
                    checked={focusMode}
                    onChange={handleFocusModeChange}
                  />
                }
                label="Focus mode"
                title="Toggle view the infrastructure components generated by the platform."
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={autoRefresh}
                    onChange={handleAutoRefreshChange}
                  />
                }
                label="Automatic statistics refresh"
                title="Toggle automatic refresh of statistics and properties from queues and exchanges from broker."
              />
            </form>
          </ListItemText>

          {topology && (
            <ButtonExportTopology topology={topology} suffix={environment} />
          )}

          <ListItemIcon>
            <Button
              onClick={handleFullScreen}
              children={<AspectRatioIcon />}
              title="Toggle fullscreen"
            />
          </ListItemIcon>
        </ListItem>

        <Divider className={classes.divider} />

        <CardContent>
          {topology ? (
            <TopologyView
              topology={topology}
              autoRefresh={autoRefresh}
              env={environment}
              fullScreen={fullScreen}
              focusMode={focusMode}
              searchValue={debouncedSearchValue}
            />
          ) : (
            <Typography>
              The selected service has not enabled messaging function in this
              environment.
            </Typography>
          )}
        </CardContent>
      </Card>
    </>
  );
}
